// بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
//
// Settings bottom sheet (B7 · timezone). Mobile-first: full-width, bottom-anchored
// sheet that centers as a ~440px card on desktop. Follows the same fixed-sheet
// centering rule used by RescheduleSheet — `left:0; right:0; margin:0 auto` (NOT
// translateX(-50%), which the briefUp animation would override and off-center).
//
// The timezone is a short curated list; the browser-resolved zone is always
// present (added at the top when it's not already in the list) and pre-selected.
// Saving calls the controller's setTimezone (→ savePreferences), which persists
// and toasts. We OWN this file — it only imports theme tokens.

import { useEffect, useMemo, useState } from 'react';
import { C, mono, SANS } from '../theme';

// A short, curated set of common zones. The operator's resolved zone is merged in
// at render time so it is always selectable even if it's outside this list.
const COMMON_ZONES: string[] = [
  'UTC',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Moscow',
  'Europe/Istanbul',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Almaty',
  'Asia/Bangkok',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Australia/Sydney',
  'America/Sao_Paulo',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
];

export function SettingsSheet({
  timezone,
  onSave,
  onClose,
}: {
  timezone: string;
  onSave: (tz: string) => Promise<void>;
  onClose: () => void;
}) {
  const [value, setValue] = useState(timezone || 'UTC');
  const [busy, setBusy] = useState(false);

  // Lock body scroll + Esc-to-close, matching the other sheets.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // Merge the current/resolved zone into the option list (dedup, keep it on top).
  const zones = useMemo(() => {
    const resolved = value || 'UTC';
    const list = COMMON_ZONES.includes(resolved) ? COMMON_ZONES : [resolved, ...COMMON_ZONES];
    return list;
  }, [value]);

  const save = async () => {
    setBusy(true);
    try {
      await onSave(value);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(26,25,22,.32)', zIndex: 210 }} />
      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          margin: '0 auto',
          width: '100%',
          maxWidth: 440,
          background: C.white,
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          boxShadow: '0 -8px 30px rgba(26,25,22,.18)',
          zIndex: 211,
          padding: '18px 18px calc(18px + env(safe-area-inset-bottom))',
          animation: 'briefUp .25s ease both',
          fontFamily: SANS,
          color: C.ink,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Настройки</div>
          <button onClick={onClose} aria-label="Закрыть" style={{ marginLeft: 'auto', background: 'none', border: 'none', fontSize: 20, color: C.faint, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={mono({ fontSize: 10.5, letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted, marginBottom: 7 })}>Часовой пояс</div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>
          Используется для отображения времени публикаций и рекомендаций по расписанию.
        </div>

        <select
          value={value}
          onChange={(e) => setValue(e.target.value)}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '11px 12px',
            border: `1px solid ${C.line2}`,
            borderRadius: 12,
            fontFamily: SANS,
            fontSize: 15,
            color: C.ink,
            background: C.white,
            marginBottom: 16,
            appearance: 'none',
            cursor: 'pointer',
          }}
        >
          {zones.map((z) => (
            <option key={z} value={z}>
              {z}
            </option>
          ))}
        </select>

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            disabled={busy}
            onClick={() => void save()}
            style={{ flex: 1, background: C.ink, color: C.white, border: 'none', borderRadius: 11, padding: '12px 14px', fontFamily: SANS, fontSize: 14, fontWeight: 600, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}
          >
            {busy ? 'Сохраняю…' : 'Сохранить'}
          </button>
          <button
            disabled={busy}
            onClick={onClose}
            style={{ background: 'none', border: `1px solid ${C.line3}`, borderRadius: 11, padding: '12px 16px', fontFamily: SANS, fontSize: 14, color: C.sub, cursor: 'pointer' }}
          >
            Отмена
          </button>
        </div>
      </div>
    </>
  );
}

export default SettingsSheet;
